/** Server-Settings (admin): sv.*-Felder gruppiert lesen/ändern/speichern. */

import { useSignal, useSignalEffect } from "@preact/signals";
import { api, activeServer } from "../store";
import { PageHead, Panel, NoServer } from "../components";
import { PanelGrid, type PanelItem } from "../grid/PanelGrid";
import { toast } from "../toast";
import { SETTINGS, SETTINGS_KEYS, type SettingField } from "../settings-defs";
import type { ApiResponse } from "../api";
import { t } from "../i18n";

const truthy = (v: string) => v === "1" || v.toLowerCase() === "true" || v.toLowerCase() === "on";

export function Settings() {
  const vals = useSignal<Record<string, string>>({});
  const edits = useSignal<Record<string, string>>({});
  const loading = useSignal(false);
  const editMode = useSignal(false);

  async function load() {
    const sv = activeServer.value;
    if (!sv) return;
    loading.value = true;
    edits.value = {};
    try {
      const r = await api.call<ApiResponse & { values: Record<string, string> }>("settings_get", { server: sv, keys: SETTINGS_KEYS });
      if (r.ok) vals.value = r.values;
    } finally {
      loading.value = false;
    }
  }
  useSignalEffect(() => { if (activeServer.value) void load(); });

  const cur = (k: string) => (k in edits.value ? edits.value[k]! : (vals.value[k] ?? ""));
  const set = (k: string, v: string) => { edits.value = { ...edits.value, [k]: v }; };
  const dirtyKeys = () => Object.keys(edits.value).filter((k) => edits.value[k] !== (vals.value[k] ?? ""));

  async function save() {
    const sv = activeServer.value;
    if (!sv) return;
    const keys = dirtyKeys();
    if (keys.length === 0) { toast(t("common.noChanges")); return; }
    const changes: Record<string, string> = {};
    for (const k of keys) changes[k] = edits.value[k]!;
    const r = await api.call<ApiResponse & { applied?: string[] | Record<string, unknown> }>("settings_set", { server: sv, changes });
    if (r.ok) {
      const n = Array.isArray(r.applied) ? r.applied.length : Object.keys(r.applied ?? {}).length;
      toast(`${n} ${t("settings.savedCountSuffix")}`, "ok");
      await load();
    }
    else toast(String(r.error ?? t("common.error")), "err");
  }

  function Field({ f }: { f: SettingField }) {
    const v = cur(f.key);
    if (f.type === "toggle") {
      return (
        <label class="row" style="justify-content:space-between;padding:6px 0">
          <span>{f.label}</span>
          <input type="checkbox" checked={truthy(v)} onChange={(e) => set(f.key, (e.target as HTMLInputElement).checked ? "1" : "0")} />
        </label>
      );
    }
    return (
      <div class="field">
        <label>{f.label}</label>
        {f.type === "textarea" ? (
          <textarea class="textarea" value={v} onInput={(e) => set(f.key, (e.target as HTMLTextAreaElement).value)} />
        ) : (
          <input class={`input ${f.type === "num" ? "mono" : ""}`} type={f.type === "num" ? "number" : "text"} value={v} onInput={(e) => set(f.key, (e.target as HTMLInputElement).value)} />
        )}
      </div>
    );
  }

  if (!activeServer.value) return (<><PageHead title={t("nav.settings")} /><NoServer /></>);

  const dirty = dirtyKeys().length;
  const items: PanelItem[] = SETTINGS.map((g) => ({
    id: `grp:${g.group}`,
    title: g.group,
    defaultSpan: 6,
    render: () => (
      <Panel title={g.group}>
        <div class="stack" style="gap:var(--s3)">
          {g.fields.map((f) => <Field key={f.key} f={f} />)}
        </div>
      </Panel>
    ),
  }));

  return (
    <div class="view-enter">
      <PageHead title={t("nav.settings")} sub={t("settings.sub")}>
        <button class={`btn sm ${editMode.value ? "primary" : ""}`} onClick={() => (editMode.value = !editMode.value)}>{editMode.value ? t("dashboard.editDone") : t("dashboard.edit")}</button>
        <button class="btn sm" onClick={() => void load()} disabled={loading.value}>↻ {t("common.reload")}</button>
        <button class="btn primary" onClick={() => void save()} disabled={dirty === 0}>{t("common.save")}{dirty ? ` (${dirty})` : ""}</button>
      </PageHead>
      <PanelGrid view="settings" items={items} edit={editMode} />
    </div>
  );
}
